import { z } from "zod";
import { idempotencyKeySchema } from "./common.js";

/**
 * Phase 14a — `account.*` procedures. The DoD-gate slice that satisfies
 * `MVP.md` §3.12 R-DATA-1 (full account export — JSON + photo archive)
 * and R-DATA-2 (account delete with SLA copy stated in policy).
 *
 * Two procedures land:
 *
 * - `account.export` — authenticated query (or query-shaped mutation;
 *   tRPC treats both the same on the wire). Returns the canonical
 *   JSON dump composed by `composeAccountExport` plus the photo
 *   archive as a base64-encoded ZIP blob. **Idempotent:** repeated
 *   calls with the same `clientNow` (or same export instant) produce
 *   byte-identical output. The procedure is not gated by the
 *   idempotency-key helper because nothing mutates — the dump is a
 *   read.
 *
 * - `account.delete` — authenticated mutation. Cascades through every
 *   repository the principal owns (sites, panels, rooms, devices,
 *   warnings, share links, photo metadata, R2 photo blobs), then
 *   calls the Clerk Backend SDK adapter to remove the Clerk row.
 *   **Idempotent:** the first call removes the user; a second call
 *   with the same idempotency key returns the cached "no-op 404"
 *   response (per the Phase 12a idempotency contract — the
 *   `idempotencyStore` row carries the original error envelope), or
 *   a fresh request without the cached key throws `NOT_FOUND`.
 *
 * The export envelope shape is intentionally minimal at the contract
 * boundary — the heavy lifting is in the domain composer and the
 * Phase 6d photo bundle builder. The contract here is the wire shape
 * the Phase 14a web surface consumes.
 */

export const accountExportInputSchema = z.object({}).optional();
export type AccountExportInput = z.infer<typeof accountExportInputSchema>;

/**
 * Wire shape for an `account.export` result. The `dump` field carries
 * the JSON-serializable composed export (see
 * `@panelgenie/domain`'s `AccountExportDump`). `photosBundleBase64`
 * is the Phase 6d photo-archive ZIP encoded as base64 so the wire
 * stays JSON; the web client decodes back to bytes for the download.
 * `bundleByteLength` lets the client surface a size hint before
 * triggering the download.
 *
 * Schema keeps `dump` as `z.unknown()` because the composed envelope
 * shape is already validated by the per-aggregate codec (Phase 6a)
 * at compose time; re-running zod over the entire nested envelope
 * would duplicate the codec's invariants and inflate the bundle's
 * schema cost for no protective gain. The web client treats `dump`
 * as opaque JSON for serialization to a download file.
 */
export const accountExportOutputSchema = z.object({
  schemaVersion: z.literal(1),
  exportedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/),
  ownerId: z.string().min(1),
  dump: z.unknown(),
  photosBundleBase64: z.string(),
  bundleByteLength: z.number().int().nonnegative(),
  photoCount: z.number().int().nonnegative(),
  siteCount: z.number().int().nonnegative(),
  /**
   * SLA copy surfaced in the export modal per `MVP.md` §4.10 step 1.
   * Returned from the server so the surface and the policy stay in
   * one place; the web client renders this verbatim.
   */
  slaCopy: z.string().min(1),
});
/**
 * `AccountExportOutput` is declared explicitly (not inferred from
 * `accountExportOutputSchema`) so the `dump` field is required at
 * the type level. Zod's `z.unknown()` infers as optional, which is
 * the correct wire-shape semantics (the schema accepts a missing
 * `dump` because `unknown` permits everything) but the *procedure*
 * always returns `dump` populated. Declaring the type by hand
 * captures the procedure's actual return shape without forcing the
 * runtime schema to over-validate.
 */
export type AccountExportOutput = {
  readonly schemaVersion: 1;
  readonly exportedAt: string;
  readonly ownerId: string;
  readonly dump: unknown;
  readonly photosBundleBase64: string;
  readonly bundleByteLength: number;
  readonly photoCount: number;
  readonly siteCount: number;
  readonly slaCopy: string;
};

/**
 * `account.delete` input. Carries the typed-confirmation phrase the
 * client collects in the delete modal (per spec — the modal requires
 * a typed-confirmation phrase to gate the irreversible action). The
 * server re-validates the phrase against the canonical literal so a
 * client bypass cannot skip the gate.
 */
export const ACCOUNT_DELETE_CONFIRMATION_PHRASE = "delete my account" as const;

export const accountDeleteInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  /**
   * The user-typed confirmation phrase. Must equal
   * `ACCOUNT_DELETE_CONFIRMATION_PHRASE` after trimming and case-
   * folding to lowercase. A mismatch returns `BAD_REQUEST` —
   * intentional shape so a clever client cannot side-step the modal.
   */
  confirmationPhrase: z.string().min(1),
});
export type AccountDeleteInput = z.infer<typeof accountDeleteInputSchema>;

export const accountDeleteOutputSchema = z.object({
  /**
   * `true` when this call cascaded the delete (Clerk row removed,
   * aggregates dropped). `false` is reserved for the "already
   * deleted" arm — the idempotency cache returned a prior `true`,
   * but the procedure surfaces this so the surface can show the
   * "your account is already deleted" copy without ambiguity.
   */
  deleted: z.boolean(),
  /**
   * The user id that was deleted. Always the authenticated principal —
   * the surface uses this to clear local caches keyed by user id.
   */
  userId: z.string().min(1),
  /** SLA copy rendered by the post-delete confirmation surface. */
  slaCopy: z.string().min(1),
});
export type AccountDeleteOutput = z.infer<typeof accountDeleteOutputSchema>;

/**
 * SLA copy strings. The `MVP.md` §4.10 step 2 text is the canonical
 * delete copy. The export copy is the §4.10 step 1 "email delivery"
 * wording adapted for the inline download flow at MVP — the v1 web
 * surface delivers the bundle inline rather than emailing it (the
 * email-delivery wiring lands when SES/transactional-mail is
 * provisioned, a Phase 14c+ infrastructure slice).
 */
export const ACCOUNT_EXPORT_SLA_COPY =
  "Your data is ready to download below. We do not retain a copy of the export on our servers." as const;

export const ACCOUNT_DELETE_SLA_COPY =
  "Deletion completes within 30 days; you'll receive a confirmation email." as const;

/**
 * Phase 14d — `account.feedback` procedure. Captures one user-submitted
 * free-text comment plus three correlation fields per the slice note:
 *
 *  - `sessionId` — the Phase 14c crash-reporter session id (verbatim;
 *    the dashboard joins on this against the crash-event stream so a
 *    piece of feedback can be cross-referenced against any crashes the
 *    same session saw). Optional at the wire boundary because a
 *    stripped-down client (CLI, SSR fallback) may not have minted one.
 *  - `tier` — the user's tier at submission time, taken verbatim from
 *    the contract-shared `tierSchema` (Phase 12c). The server pins this
 *    server-side anyway (`ctx.principal.tier`) and overwrites whatever
 *    the client submits; the input field is informational so the wire
 *    shape is symmetric (and so a future analytics consumer can see
 *    "client thought tier was X" if a desync ever surfaces).
 *  - `route` — `window.location.pathname` at submission time. Free-form
 *    string capped at 256 chars so the database column stays
 *    predictable.
 *
 * **Idempotent.** The procedure takes an `idempotencyKey` and gates on
 * the Phase 12a `withIdempotency` wrapper. Retrying with the same key
 * returns the cached response. The repository row id is minted
 * deterministically from `(ownerId, idempotencyKey)` so a belt-and-
 * braces dedupe lives inside the repository too.
 *
 * **Out of scope at 14d** (per the carried-forward note in `NEXT.md`):
 * third-party survey tooling (SurveyMonkey / Typeform); analytics on
 * submission rates (read directly off the `account.feedback` table by
 * the operator dashboard); recruitment + weekly check-ins (the
 * BizDocs README captures the operator process).
 */

export const FEEDBACK_MESSAGE_MAX_CHARS = 4000 as const;
export const FEEDBACK_ROUTE_MAX_CHARS = 256 as const;

export const accountFeedbackInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  /**
   * Phase 14c crash-reporter session id. The web client passes
   * `crashReporter.currentSessionId()`; the field is optional so a
   * client without a live session (SSR fallback, CLI) can still
   * submit.
   */
  sessionId: z.string().min(1).max(128).optional(),
  /**
   * Free-text comment. Capped at 4000 chars — enough for a few
   * paragraphs, well under the database `text` column's safety limit.
   * An empty string is rejected at the contract boundary.
   */
  message: z.string().min(1).max(FEEDBACK_MESSAGE_MAX_CHARS),
  /**
   * `window.location.pathname` at submission time. Capped at 256 chars
   * so the database column stays predictable; the wire-shape is
   * free-form (we do not parse it).
   */
  route: z.string().min(1).max(FEEDBACK_ROUTE_MAX_CHARS),
});
export type AccountFeedbackInput = z.infer<typeof accountFeedbackInputSchema>;

export const accountFeedbackOutputSchema = z.object({
  /**
   * Stable feedback row id assigned at submission time. Mints
   * deterministically from `(ownerId, idempotencyKey)` so a retry
   * collapses onto the same row.
   */
  id: z.string().min(1),
  /** ISO 8601 UTC instant the row was written. */
  submittedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/),
  /**
   * Static "thanks, we saw it" copy the surface renders verbatim once
   * the mutation resolves. Lives in the contract so the policy and the
   * surface stay in one place (mirrors `ACCOUNT_EXPORT_SLA_COPY` and
   * `ACCOUNT_DELETE_SLA_COPY`).
   */
  thanksCopy: z.string().min(1),
});
export type AccountFeedbackOutput = z.infer<typeof accountFeedbackOutputSchema>;

/**
 * Confirmation copy rendered after a successful feedback submission.
 * Verbatim per `MVP.md` §6 DoD item 8's tone ("five reference users
 * confirmed they would recommend it") — the affordance is a beta
 * channel, the response acknowledges receipt without promising a
 * personal reply.
 */
export const ACCOUNT_FEEDBACK_THANKS_COPY =
  "Thanks — we read every note while we're polishing the beta." as const;
